import type { HomeContent } from "./home";

// Verbatim content lifted from the original hardcoded Home.tsx. Used as the
// baked-in default so the website renders identically before any CMS content
// exists, and as the seed/fallback when the API is unreachable.
export const defaultHomeContent: HomeContent = {
  meta: {
    title: "Quintara",
    description:
      "Quintara is a business messaging platform on WhatsApp for enterprises, governments, agencies, mobile operators and SMBs across the Middle East, Africa and beyond.",
  },
  hero: {
    eyebrow: "",
    headingLead: "Reach customers on the app they",
    headingHighlight: "open every day.",
    lede: "Quintara moves your one-time passcodes, notifications, sales and customer care onto WhatsApp, with operator-grade infrastructure, and measurable results.",
    ctas: [
      { label: "Talk to our team", to: "/contact", variant: "primary", arrow: true },
      { label: "Explore the platform", to: "/platform", variant: "ghost", arrow: false },
    ],
  },
  backers: {
    label: "A venture of",
    partners: [
      { name: "Numbase Group", description: "Telecom and AI infrastructure", href: "https://numbase.com" },
      { name: "JGroup", description: "Media, data and agency distribution", href: "https://jgroup-me.com" },
    ],
  },
  whyQuintara: {
    eyebrow: "Why Quintara",
    heading: "Messaging technology and market reach, in one partner.",
    lede: "Most messaging providers have the technology but no local reach, or the reach but no technology. Quintara combines Numbase's operator rails with JGroup's agency and media network, so businesses get the platform and the people on the ground to make it work.",
    figures: [
      { value: "35", caption: "countries with local teams across the Gulf, Levant, Africa, Asia and Europe" },
      { value: "130+", caption: "mobile operator integrations for distribution and carrier billing" },
      { value: "200+", caption: "independent agencies, plus all six global holding groups" },
    ],
  },
  migrationPath: {
    eyebrow: "The migration path",
    heading: "From SMS to conversations, one use case at a time.",
    lede: "We start with the messages your business already sends and can't stop sending, then add richer journeys once the channel is proven.",
    steps: [
      { title: "Authentication", description: "One-time passcodes and login verification, with SMS fallback where needed." },
      { title: "Utility notifications", description: "Order, delivery, billing, booking and account alerts." },
      { title: "Service messages", description: "Two-way updates customers can reply to inside the same thread." },
      { title: "Lead qualification", description: "AI agents that ask the right questions and push qualified leads to your CRM." },
      { title: "Customer care", description: "Automated answers with full-context handoff to your human agents." },
      { title: "Marketing reactivation", description: "Opt-in campaigns, catalogs and Click-to-WhatsApp ads tied to sales." },
    ],
  },
  arabicAi: {
    eyebrow: "Arabic-first AI",
    heading: "Agents that speak the way your customers do.",
    lede: "Our conversational AI supports multiple languages and dialects. Every agent answers only from your own catalog, prices and policies, and hands over to a person when it should.",
    checks: [
      { boldLead: "Grounded answers.", text: "No invented prices or stock levels. Questions it can't answer go to your team." },
      { boldLead: "Handoff with context.", text: "Your agent sees the full thread, the intent and the CRM record." },
      { boldLead: "Tested per dialect.", text: "Agents are evaluated on dialect-specific test sets before they go live." },
    ],
    linkText: "How the AI layer works",
    linkTo: "/platform#ai",
    phone: {
      businessName: "Al Noor Motors",
      accountLabel: "Business account",
      ariaLabel:
        "Example WhatsApp conversation in Gulf Arabic between a customer and a car dealer's AI agent, ending with a test drive booking synced to the CRM",
      thread: [
        { kind: "msg", dir: "in", text: "مرحبا، أبي أعرف عن عروض سيارات الدفع الرباعي هالشهر", time: "10:02" },
        { kind: "msg", dir: "out", text: "هلا فيك! عندنا تمويل بدون دفعة أولى لين نهاية الشهر. تبي تحجز تجربة قيادة؟", time: "10:02" },
        { kind: "chips", items: ["احجز تجربة قيادة", "تفاصيل التمويل", "كلّمني موظف"] },
        { kind: "msg", dir: "in", text: "احجز تجربة قيادة يوم السبت الصبح", time: "10:03" },
        { kind: "msg", dir: "out", text: "تم الحجز: السبت الساعة ١١ صباحاً، فرع شارع الشيخ زايد. بيوصلك تذكير قبلها بيوم.", time: "10:03" },
        { kind: "handoff", text: "Lead qualified · synced to CRM · assigned to sales" },
      ],
      caption: "Example conversation for a fictional car dealer.",
    },
  },
  whoWeWorkWith: {
    eyebrow: "Who we work with",
    heading: "Built for every route to market.",
    segments: [
      {
        tag: "Enterprise and government",
        heading: "Dedicated onboarding for high-volume, high-trust messaging.",
        description: "Named commercial and technical teams, data kept in-region, and service messaging for citizens and customers.",
        to: "/solutions#enterprise",
      },
      {
        tag: "Agencies",
        heading: "Add WhatsApp to the media plans you already run.",
        description: "Click-to-WhatsApp ads, conversion tracking and one setup you can roll out across your advertiser book.",
        to: "/solutions#agencies",
      },
      {
        tag: "Mobile operators",
        heading: "Upgrade A2P traffic and open a new revenue line.",
        description: "Migrate SMS traffic, resell WhatsApp messaging to your business customers, and bill through your existing rails.",
        to: "/solutions#operators",
      },
      {
        tag: "Small and medium businesses",
        heading: "Go live in minutes, without a developer.",
        description: "Guided setup that handles verification, templates and your first automated replies, with local billing options.",
        to: "/solutions#smb",
      },
    ],
  },
  qualityFeatures: [
    {
      heading: "Quality first",
      description: "We monitor quality ratings, block rates and opt-outs on every number, and throttle risky sends before they hurt your account.",
    },
    {
      heading: "Measured like media",
      description: "Conversions API, Click-to-WhatsApp attribution and cost-per-conversation reporting connect messaging to revenue.",
    },
    {
      heading: "Data stays in region",
      description: "Hosted in AWS UAE and Bahrain for Gulf clients, with tenant isolation and encryption in transit and at rest.",
    },
  ],
  ctaBand: {
    eyebrow: "Get in touch",
    heading: "Ready to move your customer messaging to WhatsApp?",
    lede: "Tell us your markets, volumes and use cases. A solutions lead will come back to you within one business day.",
    buttonLabel: "Talk to our team",
    buttonTo: "/contact",
  },
};
